import { useTranslation } from "react-i18next";
import { Mail01 } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { EveLogo } from "@/components/foundations/logo/eve-logo";
import { LanguageSelector } from "@/components/application/language-selector/language-selector";
import { VersionFooter } from "@/components/base/version-footer/version-footer";
import { useMe, getRedirectPath } from "@/hooks/api/use-auth";

interface LegalPageLayoutProps {
    children: React.ReactNode;
}

export function LegalPageLayout({ children }: LegalPageLayoutProps) {
    const { t } = useTranslation("pages:landing");
    const { data: currentUser } = useMe();
    const env = import.meta.env.VITE_ENV;
    const hasEnvironmentBanner = env !== "production";

    return (
        <div className={`min-h-screen bg-secondary relative ${hasEnvironmentBanner ? "pt-7" : ""}`}>
            {/* Header */}
            <header className="relative z-20 border-b border-secondary_alt bg-primary/80 backdrop-blur-sm">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex h-14 sm:h-16 items-center justify-between">
                        <a href="/" className="flex items-center gap-2 sm:gap-3">
                            <EveLogo className="h-6 w-6 sm:h-8 sm:w-8" />
                            <h3 className="text-lg sm:text-2xl font-bold text-primary">Eve Mind</h3>
                        </a>
                        <div className="flex items-center gap-2 sm:gap-4">
                            {currentUser ? (
                                <Button
                                    href={getRedirectPath(currentUser) ?? "/auth/login"}
                                    size="sm"
                                    color="primary"
                                    className="sm:!size-md"
                                >
                                    {t("hero.ctaGoToApp")}
                                </Button>
                            ) : (
                                <Button href="/auth/login" size="sm" color="primary" className="sm:!size-md">
                                    {t("hero.ctaLogin")}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Main content */}
            <main className="relative z-10 py-12 sm:py-16">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">{children}</div>
            </main>

            {/* Footer */}
            <footer className="relative z-10 border-t border-secondary_alt bg-primary/95 backdrop-blur-sm py-12 sm:py-16">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl text-center">
                        <h2 className="text-xl font-bold text-primary sm:text-2xl lg:text-3xl px-4 sm:px-0">
                            {t("footer.contactTitle")}
                        </h2>
                        <p className="mt-3 sm:mt-4 text-sm sm:text-base text-quaternary px-4 sm:px-0">
                            {t("footer.contactDescription")}
                        </p>
                        <div className="mt-6 sm:mt-8 flex flex-col items-stretch sm:items-center justify-center gap-3 sm:gap-4 px-4 sm:px-0">
                            <Button
                                href="mailto:eve.mind.ai@gmail.com"
                                size="lg"
                                color="primary"
                                iconLeading={Mail01}
                                className="w-full sm:w-auto"
                            >
                                {t("footer.contactButton")}
                            </Button>
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 text-xs sm:text-sm text-quaternary">
                                <span>{t("footer.loginLink")}</span>
                                <Button href="/auth/login" size="sm" color="link-color">
                                    {t("footer.loginButton")}
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className="mt-8 sm:mt-12 border-t border-secondary_alt pt-6 sm:pt-8">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div className="flex justify-center sm:justify-start">
                                <LanguageSelector showLabel={false} />
                            </div>
                            <p className="text-xs sm:text-sm text-quaternary text-center sm:text-right">
                                © {new Date().getFullYear()} Eve Mind. All rights reserved.
                            </p>
                        </div>
                        <div className="mt-4">
                            <VersionFooter />
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
